import React from 'react';
import { ActionIcon, Anchor, Divider, Group, Indicator, Popover, ScrollArea, Text, Tooltip, rem } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconBell, IconInbox } from '@tabler/icons-react';
import { motion, useReducedMotion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import EmptyState from '@/components/ui/Empty.State';
import ItemList from '@/components/ui/list/Item.List';
import { TOUCH_TARGET, useIsPhone, useIsTouch } from '@/hooks/use.responsive';
import { notify } from '@/lib/notify/notify';
import { selectSessionUser } from '@/store/auth/auth.selectors';
import {
    useListNotificationsQuery, useMoreNotificationsMutation, useReadAllNotificationsMutation, useReadNotificationMutation,
} from '@/store/api/notification/notification.api';
import { useAppSelector } from '@/store/store.hooks';
import type { TypeNotification } from '@/types/notification/types.notification';
import ShellNotificationsItem from './Shell.Notifications.Item';

const keyOf = (item: TypeNotification) => item.id;

const renderItem = (item: TypeNotification) => <ShellNotificationsItem item={item} />;

const ShellNotifications: React.FC = () => {
    const navigate = useNavigate();
    const reduced = useReducedMotion();
    const isTouch = useIsTouch();
    const isPhone = useIsPhone();
    const user = useAppSelector(selectSessionUser);
    const [opened, { toggle, close }] = useDisclosure(false);

    const query = useListNotificationsQuery(undefined, { skip: !user });
    const [readOne] = useReadNotificationMutation();
    const [readAll, readAllState] = useReadAllNotificationsMutation();
    const [more, moreState] = useMoreNotificationsMutation();

    const items = React.useMemo(() => query.data?.items ?? [], [query.data]);
    const unread = query.data?.unread ?? 0;
    const realtime = user?.preferences?.notifications?.realtime !== false;

    const seen = React.useRef<Set<string> | null>(null);
    React.useEffect(() => {
        if (!query.data) return;
        if (!seen.current) {
            seen.current = new Set(items.map((item) => item.id));
            return;
        }
        const fresh = items.filter((item) => !seen.current!.has(item.id));
        for (const item of fresh) seen.current.add(item.id);
        if (!realtime) return;
        for (const item of fresh.filter((entry) => !entry.read).slice(0, 3)) {
            notify.notice(item.severity, item.title, item.body, item.path ? () => navigate(item.path) : undefined);
        }
    }, [items, query.data, realtime, navigate]);

    const open = (item: TypeNotification) => {
        if (!item.read) void readOne(item.id);
        close();
        if (item.path) navigate(item.path);
    };

    const markAll = async () => {
        try {
            await readAll().unwrap();
        } catch (error) {
            notify.fromRejection(null, error);
        }
    };

    const label = unread > 0 ? `Notifiche, ${unread} da leggere` : 'Notifiche';
    const page = query.data?.page;

    return (
        <Popover
            opened={opened}
            onChange={(next) => (next ? undefined : close())}
            position="bottom-end"
            width={isPhone ? 'calc(100vw - 1.5rem)' : 400}
            shadow="lg"
            radius="lg"
            offset={8}
            withinPortal
            transitionProps={{ transition: 'fade-down', duration: 180 }}
        >
            <Popover.Target>
                <Tooltip label={label} disabled={opened}>
                    <Indicator
                        label={unread > 9 ? '9+' : unread}
                        size={16}
                        offset={5}
                        color="danger"
                        disabled={unread === 0}
                        styles={{ indicator: { fontSize: rem(10), fontWeight: 700, padding: `0 ${rem(4)}` } }}
                    >
                        <ActionIcon
                            size={isTouch ? TOUCH_TARGET : 'lg'}
                            variant="default"
                            onClick={toggle}
                            aria-label={label}
                            aria-expanded={opened}
                        >
                            <motion.span
                                style={{ display: 'flex' }}
                                animate={opened && !reduced ? { rotate: [0, -12, 10, -6, 0] } : { rotate: 0 }}
                                transition={{ duration: 0.4 }}
                            >
                                <IconBell size={19} stroke={1.7} />
                            </motion.span>
                        </ActionIcon>
                    </Indicator>
                </Tooltip>
            </Popover.Target>

            <Popover.Dropdown p={0} style={{ overflow: 'hidden' }}>
                <Group justify="space-between" px="md" py="sm" wrap="nowrap">
                    <Group gap={8} wrap="nowrap">
                        <Text fw={650} size="sm" style={{ color: 'var(--bd-text-strong)' }}>Notifiche</Text>
                        {unread > 0 ? <Text size="xs" c="dimmed">{unread} da leggere</Text> : null}
                    </Group>
                    {unread > 0 ? (
                        <Anchor component="button" type="button" size="xs" fw={500} onClick={() => { void markAll(); }} disabled={readAllState.isLoading}>
                            Segna tutte come lette
                        </Anchor>
                    ) : null}
                </Group>
                <Divider />

                <ScrollArea.Autosize mah={rem(440)} type="scroll" scrollbarSize={6} className="bd-scroll-block">
                    <ItemList
                        label="Notifiche"
                        items={items}
                        itemKey={keyOf}
                        itemLabel={(item) => `${item.read ? '' : 'Non letta. '}${item.title}`}
                        renderItem={renderItem}
                        onItemClick={open}
                        frame="plain"
                        density="compact"
                        loading={query.isLoading}
                        skeleton={{ rows: 3 }}
                        error={query.isError && !query.data ? {
                            title: 'Le notifiche non sono arrivate',
                            body: 'Il server non ha risposto. Riprova fra poco.',
                            onRetry: () => { void query.refetch(); },
                            retrying: query.isFetching,
                        } : null}
                        more={page?.hasMore && page.nextCursor ? {
                            hasMore: true,
                            loading: moreState.isLoading,
                            failed: moreState.isError,
                            onMore: () => { void more(page.nextCursor as string); },
                            label: 'Mostra le precedenti',
                        } : undefined}
                        empty={(
                            <EmptyState
                                compact
                                icon={IconInbox}
                                title="Nessuna novità"
                                body="Qui arrivano gli avvisi dei server e gli esiti dei deploy."
                            />
                        )}
                    />
                </ScrollArea.Autosize>
            </Popover.Dropdown>
        </Popover>
    );
};

export default ShellNotifications;
